// src/services/userService.ts

import { apiService } from './apiService';
import { Users, UserPayload } from '../dtos/Users';
import axios, { AxiosError } from 'axios';
import { ApiError } from './apiError';
import { ServiceResponse } from './serviceResponse';


/**
 * Manejador de errores centralizado.
 * Convierte un error (potencialmente de Axios) en nuestro objeto ApiError estandarizado.
 * @param err - El error capturado en el bloque catch.
 * @returns Un objeto ApiError.
 */
const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    // Si el backend envía un objeto de error con 'message', úsalo.
    const errorMessage = error.response?.data?.message || 'Ocurrió un error en el servidor.';
    return {
      message: errorMessage,
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  // Para errores que no son de Axios (ej. de red, de código, etc.)
  return {
    message: 'Ocurrió un error inesperado. Revisa tu conexión de red.',
  };
};

/**
 * Obtiene la lista completa de usuarios.
 */
const getAll = async (): Promise<ServiceResponse<Users[]>> => {
  try {
    const users = await apiService.get<Users[]>('/api/Usuarios');
    return { data: users, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

/** Obtiene usuarios filtrados por sucursal (Swagger: GET /api/Usuarios/Filtered?sucursalId=) */
const getFilteredBySucursal = async (
  sucursalId: number
): Promise<ServiceResponse<Users[]>> => {
  try {
    const users = await apiService.get<Users[]>("/api/Usuarios/Filtered", {
      params: { sucursalId },
    });
    return { data: users, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};
/**
 * Obtiene un usuario por su ID.
 */
const getById = async (id: number): Promise<ServiceResponse<Users>> => {
  try {
    const user = await apiService.get<Users>(`/api/Usuarios/${id}`);
    return { data: user, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

/**
 * Crea un nuevo usuario.
 */
const create = async (payload: UserPayload): Promise<ServiceResponse<Users>> => {
  try {
    const newUser = await apiService.post<Users, UserPayload>('/api/Usuarios', payload);
    return { data: newUser, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

/**
 * Actualiza un usuario existente.
 */
const update = async (id: number, payload: Partial<UserPayload>): Promise<ServiceResponse<Users>> => {
  try {
    const updatedUser = await apiService.put<Users, Partial<UserPayload>>(`/api/Usuarios/${id}`, payload);
    return { data: updatedUser, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

const Delete = async (id: number, payload: Partial<UserPayload>): Promise<ServiceResponse<Users>> => {
  try {
    const updatedUser = await apiService.put<Users, Partial<UserPayload>>(`/api/Usuarios/${id}`, payload);
    return { data: updatedUser, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};
/**
 * Elimina un usuario.
 */
const remove = async (id: number): Promise<ServiceResponse<null>> => {
  try {
    // La respuesta en un DELETE exitoso a menudo no tiene cuerpo (data).
    await apiService.delete<void>(`/api/Usuarios/${id}`);
    return { data: null, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

// Exportamos todas las funciones del servicio de usuario.
export const userService = {
  getFilteredBySucursal,
  getAll,
  getById,
  create,
  update,
  Delete,
  delete: remove,
};